/**
 * HarborPhotoUpload
 *
 * A self-contained photo-picker/uploader for Harbor submission screens.
 *
 * Flow:
 *  1. User picks a file (web file-input or native camera/gallery).
 *  2. File is validated (JPEG/PNG, ≤ 10 MB).
 *  3. File is uploaded via POST /api/perfect-truck/images/upload
 *     with containerName = "harbor".
 *  4. On success the parent receives { fileName, originalFileName, imageUrl }
 *     via `onUploaded`.  The caller is responsible for JSON.stringify-ing
 *     { fileName, originalFileName } into the submission payload's
 *     `imageResponse` field.
 *
 * Usage:
 *   <HarborPhotoUpload
 *     label="Attach Photo"
 *     onUploaded={(result) => setUploadResult(result)}
 *   />
 */
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Modal,
  Dimensions,
  Pressable,
  Alert,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { launchImageLibrary, launchCamera } from 'react-native-image-picker';
import Svg, { Rect } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import { FontFamily } from '../../../constants/fonts';
import { AdminColors } from '../../Admin/constants/adminColors';
import { WebImage } from '../../PerfectTruck/PTPhotoUpload';
import {
  uploadHarborImage,
  HarborImageUploadResult,
} from '../../../api/deliveryRpmApi';
import { useDarkStyles } from '../../../theme/useDarkStyles';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_BOX_WIDTH = 420;
const MAX_BOX_HEIGHT = 315;

const isWeb =
  typeof (globalThis as any).document !== 'undefined' && Platform.OS === 'web';

// ─── Public types ─────────────────────────────────────────────────────────────

/** The value passed back to the parent after a successful upload. */
export type { HarborImageUploadResult };

// ─── Props ────────────────────────────────────────────────────────────────────

interface HarborPhotoUploadProps {
  /** Label shown beneath the photo box. */
  label?: string;
  /** Called with the server upload result after a successful upload. */
  onUploaded: (result: HarborImageUploadResult) => void;
  /** Aspect ratio for the photo box. Defaults to 4/3. */
  aspectRatio?: number;
  /** Optional initial preview URI (e.g. for edit flows). */
  initialPreviewUri?: string;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

const validateFile = (
  type: string,
  size: number,
  tJpegPngOnly: string,
  tFileTooLarge: string,
): string | null => {
  if (!ALLOWED_TYPES.includes(type.toLowerCase())) {
    return tJpegPngOnly;
  }
  if (size > MAX_FILE_SIZE) {
    return tFileTooLarge;
  }
  return null;
};

// ─── Component ────────────────────────────────────────────────────────────────

const HarborPhotoUpload: React.FC<HarborPhotoUploadProps> = ({
  label = 'Photo',
  onUploaded,
  aspectRatio,
  initialPreviewUri,
}) => {
  const styles = useDarkStyles(baseStyles);
  const { t } = useTranslation('harbor');

  const [previewUri, setPreviewUri] = useState<string | null>(
    initialPreviewUri ?? null,
  );
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [boxSize, setBoxSize] = useState<{ w: number; h: number } | null>(null);

  const hasImage = !!previewUri && !uploading;
  const windowDimensions = Dimensions.get('window');
  const modalImageSize = Math.min(
    windowDimensions.width * 0.9,
    windowDimensions.height * 0.7,
  );

  // ── Core upload ─────────────────────────────────────────────────────────────

  const doUpload = async (
    file: File | { uri: string; name: string; type: string },
    localPreviewUri: string,
  ) => {
    setUploading(true);
    setProgress(0);
    setError(null);
    try {
      const result = await uploadHarborImage(file, p => setProgress(p));
      setPreviewUri(localPreviewUri);
      setProgress(1);
      onUploaded(result);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ??
          e?.message ??
          t('haulback_submit.photo_upload_error_upload_failed'),
      );
      // Keep any prior preview so the user can see what was attempted
    } finally {
      setUploading(false);
    }
  };

  // ── Pickers ──────────────────────────────────────────────────────────────────

  const pickWeb = () => {
    const input = (globalThis as any).document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png';
    input.onchange = async (event: any) => {
      const file: File = event.target.files?.[0];
      if (!file) return;
      const validationError = validateFile(
        file.type,
        file.size,
        t('haulback_submit.photo_upload_error_jpeg_png_only'),
        t('haulback_submit.photo_upload_error_file_too_large'),
      );
      if (validationError) {
        setError(validationError);
        return;
      }
      const objectUrl = URL.createObjectURL(file);
      await doUpload(file, objectUrl);
    };
    input.click();
  };

  const launchPicker = async (source: 'camera' | 'gallery') => {
    try {
      const options = { mediaType: 'photo' as const, quality: 1 as const };
      const result =
        source === 'camera'
          ? await launchCamera(options)
          : await launchImageLibrary(options);
      if (result.didCancel) return;

      // react-native-image-picker returns errorCode on the result object
      // (not as a thrown exception) when permission is denied or camera unavailable.
      if (result.errorCode === 'permission') {
        setError(
          source === 'camera'
            ? t('haulback_submit.photo_upload_error_camera_denied')
            : t('haulback_submit.photo_upload_error_gallery_denied'),
        );
        return;
      }
      if (result.errorCode === 'camera_unavailable') {
        setError(t('haulback_submit.photo_upload_error_camera_unavailable'));
        return;
      }
      if (result.errorMessage) {
        setError(result.errorMessage);
        return;
      }

      if (result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        const type = asset.type ?? 'image/jpeg';
        const size = asset.fileSize ?? 0;
        const validationError = validateFile(
          type,
          size,
          t('haulback_submit.photo_upload_error_jpeg_png_only'),
          t('haulback_submit.photo_upload_error_file_too_large'),
        );
        if (validationError) {
          setError(validationError);
          return;
        }
        await doUpload(
          { uri: asset.uri!, name: asset.fileName ?? 'photo.jpg', type },
          asset.uri!,
        );
      }
    } catch (err: any) {
      if (err?.code === 'camera_unavailable' || err?.code === 'permission') {
        setError(
          source === 'camera'
            ? t('haulback_submit.photo_upload_error_camera_denied')
            : t('haulback_submit.photo_upload_error_gallery_denied'),
        );
      } else {
        setError(
          source === 'camera'
            ? t('haulback_submit.photo_upload_error_camera_open')
            : t('haulback_submit.photo_upload_error_gallery_open'),
        );
      }
    }
  };

  const triggerPicker = () => {
    setError(null);
    if (isWeb) pickWeb();
    else {
      Alert.alert(
        t('haulback_submit.photo_upload_add_photo_title'),
        t('haulback_submit.photo_upload_add_photo_message'),
        [
          { text: t('haulback_submit.photo_upload_take_photo'), onPress: () => launchPicker('camera') },
          { text: t('haulback_submit.photo_upload_choose_gallery'), onPress: () => launchPicker('gallery') },
          { text: t('haulback_submit.photo_upload_cancel'), style: 'cancel' },
        ],
      );
    }
  };

  // ── Box press: show preview if image ready, else open picker ────────────────

  const handleBoxPress = () => {
    if (uploading) return;
    if (hasImage) setShowModal(true);
    else triggerPicker();
  };

  const handleRePickPress = (e: any) => {
    e.stopPropagation?.();
    if (uploading) return;
    triggerPicker();
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <View style={styles.wrapper}>
        <TouchableOpacity
          style={[
            styles.box,
            aspectRatio !== undefined && { aspectRatio },
            hasImage && styles.boxFilled,
            !!error && styles.boxError,
            !isWeb && !hasImage && !error && styles.boxNoBorder,
          ]}
          onPress={handleBoxPress}
          activeOpacity={0.75}
          onLayout={e => {
            const { width, height } = e.nativeEvent.layout;
            if (
              width > 0 &&
              height > 0 &&
              (boxSize?.w !== width || boxSize?.h !== height)
            ) {
              setBoxSize({ w: width, h: height });
            }
          }}
          accessibilityRole="button"
          accessibilityLabel={
            hasImage
              ? `${label} — ${t('haulback_submit.photo_upload_tap_to_preview')}`
              : `${t('haulback_submit.photo_upload_attach_label')} ${label}`
          }
        >
          {/* SVG dashed border on native (web uses CSS borderStyle 'dashed') */}
          {!isWeb && !hasImage && !error && boxSize && (
            <Svg
              width={boxSize.w}
              height={boxSize.h}
              style={StyleSheet.absoluteFill}
              pointerEvents="none"
            >
              <Rect
                x={1}
                y={1}
                width={boxSize.w - 2}
                height={boxSize.h - 2}
                rx={10}
                ry={10}
                fill="none"
                stroke={AdminColors.borderDefault}
                strokeWidth={2}
                strokeDasharray="6,4"
              />
            </Svg>
          )}

          {uploading ? (
            <View style={styles.uploadingState}>
              <ActivityIndicator size="small" color={AdminColors.blue700} />
              <Text style={styles.progressText}>
                {Math.round(progress * 100)}%
              </Text>
            </View>
          ) : hasImage ? (
            <>
              <WebImage
                key={previewUri!}
                uri={previewUri!}
                style={styles.preview}
                resizeMode="cover"
              />
              {/* Re-pick overlay — tapping the camera icon re-triggers the picker */}
              <TouchableOpacity
                style={styles.rePickOverlay}
                onPress={handleRePickPress}
                activeOpacity={0.8}
                accessibilityLabel={t('haulback_submit.photo_upload_replace_photo')}
              >
                <MaterialIcons name="photo-camera" size={18} color="#fff" />
              </TouchableOpacity>
            </>
          ) : (
            <View style={styles.emptyState}>
              <MaterialIcons
                name="add-a-photo"
                size={28}
                color={error ? AdminColors.red400 : AdminColors.blue700}
              />
              <Text style={[styles.emptyHint, !!error && styles.emptyHintError]}>
                {isWeb
                  ? t('haulback_submit.photo_upload_select_file')
                  : t('haulback_submit.photo_upload_attach_photo')}
              </Text>
            </View>
          )}
        </TouchableOpacity>

        <Text
          style={[styles.label, hasImage && styles.labelFilled]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {label}
        </Text>

        {!!error && (
          <Text style={styles.errorText} numberOfLines={2}>
            {error}
          </Text>
        )}
      </View>

      {/* Full-screen preview modal */}
      <Modal
        visible={showModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowModal(false)}
      >
        <Pressable
          style={styles.modalBackdrop}
          onPress={() => setShowModal(false)}
        >
          <View style={styles.modalContent}>
            <WebImage
              key={previewUri!}
              uri={previewUri!}
              style={[
                styles.modalImage,
                { width: modalImageSize, height: modalImageSize },
              ]}
              resizeMode="contain"
            />
            <TouchableOpacity
              style={styles.modalCloseBtn}
              onPress={() => setShowModal(false)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <MaterialIcons name="close" size={22} color="#fff" />
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const baseStyles = StyleSheet.create({
  wrapper: {
    alignSelf: 'stretch',
  },
  box: {
    width: '100%',
    aspectRatio: 4 / 3,
    maxWidth: MAX_BOX_WIDTH,
    maxHeight: MAX_BOX_HEIGHT,
    alignSelf: 'center',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: AdminColors.blue700,
    borderRadius: 10,
    backgroundColor: AdminColors.blue25,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  boxFilled: {
    borderStyle: 'solid',
    borderColor: AdminColors.green600,
    backgroundColor: AdminColors.green75,
  },
  boxError: {
    borderStyle: 'dashed',
    borderColor: AdminColors.red400,
    backgroundColor: AdminColors.red75,
  },
  boxNoBorder: {
    borderWidth: 0,
  },
  emptyState: {
    alignItems: 'center',
    gap: 8,
  },
  emptyHint: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: AdminColors.blue700,
  },
  emptyHintError: {
    color: AdminColors.red400,
  },
  uploadingState: {
    alignItems: 'center',
    gap: 8,
  },
  progressText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: AdminColors.blue700,
  },
  preview: {
    width: '100%',
    height: '100%',
  },
  rePickOverlay: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: AdminColors.textMuted,
    textAlign: 'center',
    marginTop: 6,
  },
  labelFilled: {
    color: AdminColors.green600,
  },
  errorText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    color: AdminColors.red400,
    textAlign: 'center',
    marginTop: 4,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalImage: {
    borderRadius: 8,
  },
  modalCloseBtn: {
    position: 'absolute',
    top: -40,
    right: -4,
    backgroundColor: 'rgba(0,0,0,0.45)',
    borderRadius: 20,
    padding: 4,
  },
});

export default HarborPhotoUpload;
